/**
 * JSON-LD Schema Builders
 *
 * Pure functions that map property data and collections to schema.org
 * JSON-LD fragments. Each builder returns the fragment or null if the
 * required source data is missing.
 */

import type { Property, Amenity, Floorplan, OfficeHours, Review, SpecialOffer, PhotoGallery } from '@vibesites/schema';
import { normalizeMediaUrl } from '@vibesites/ui-primitives';

// ============================================================================
// Helpers
// ============================================================================

function defined<T>(v: T | null | undefined): v is T {
  return v != null && v !== '' && v !== 'false';
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function mediaUrl(url: string | null | undefined, width = 1280): string | null {
  if (!defined(url)) return null;
  return normalizeMediaUrl(url, { width });
}

// ============================================================================
// Core
// ============================================================================

export interface CoreOverrides {
  name?: string;
  description?: string;
  telephone?: string;
  url?: string;
  image?: string;
}

export function buildCoreSchema(
  property: Property,
  types: string[],
  overrides: CoreOverrides,
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': types.length === 1 ? types[0] : types,
  };

  const name = overrides.name || property.property_name;
  if (defined(name)) schema.name = name;

  const description = overrides.description || property.property_short_description;
  if (defined(description)) schema.description = description;

  const url = overrides.url || property.property_website_url;
  if (defined(url)) schema.url = url;

  const telephone = overrides.telephone || property.property_phone;
  if (defined(telephone)) schema.telephone = telephone;

  const image = mediaUrl(overrides.image || property.property_featured_image);
  if (defined(image)) schema.image = image;

  // Note: `yearBuilt` and an aggregate `floorSize` are intentionally NOT set on
  // this node. schema.org only allows them on Accommodation/FloorPlan, so on an
  // ApartmentComplex they are flagged invalid (fleet-audit root cause).
  if (property.property_ispetfriendly != null) {
    schema.petsAllowed = Boolean(property.property_ispetfriendly);
  }

  // schema.org exposes availability as `numberOfAvailableAccommodationUnits`
  // (there is no `numberOfAvailableAccommodation` term) and it expects a
  // QuantitativeValue, valid on ApartmentComplex / FloorPlan.
  const availableUnits = num(property.property_available_units);
  if (availableUnits) {
    schema.numberOfAvailableAccommodationUnits = {
      '@type': 'QuantitativeValue',
      value: availableUnits,
    };
  }

  const priceMin = num(property.property_price_min);
  const priceMax = num(property.property_price_max);
  if (priceMin && priceMax) {
    schema.priceRange = `$${priceMin} - $${priceMax}`;
  }

  return schema;
}

// ============================================================================
// Address
// ============================================================================

export function buildAddress(property: Property): Record<string, unknown> | null {
  const street = property.property_address_1;
  const city = property.property_address_city;
  if (!defined(street) && !defined(city)) return null;

  const address: Record<string, unknown> = { '@type': 'PostalAddress' };
  if (defined(street)) address.streetAddress = street;
  if (defined(city)) address.addressLocality = city;
  if (defined(property.property_address_state)) address.addressRegion = property.property_address_state;
  if (defined(property.property_address_zip)) address.postalCode = property.property_address_zip;
  if (defined(property.property_address_country)) address.addressCountry = property.property_address_country;
  return address;
}

// ============================================================================
// Geo
// ============================================================================

export function buildGeo(property: Property): Record<string, unknown> | null {
  const lat = num(property.property_address_latitude);
  const lng = num(property.property_address_longitude);
  if (!lat || !lng) return null;

  return {
    '@type': 'GeoCoordinates',
    latitude: lat,
    longitude: lng,
  };
}

// ============================================================================
// Social Links
// ============================================================================

const SOCIAL_FIELDS = [
  'property_facebook_url',
  'property_instagram_url',
  'property_twitter_url',
  'property_youtube_url',
  'property_tiktok_url',
  'property_linkedin_url',
] as const;

export function buildSocialLinks(property: Property): string[] | null {
  const links = SOCIAL_FIELDS
    .map(f => property[f] as string | undefined)
    .filter(defined);
  return links.length > 0 ? links : null;
}

// ============================================================================
// Has Map
// ============================================================================

export function buildHasMap(property: Property): string | null {
  return (property.property_google_place_url as string) ||
    (property.property_google_business_url as string) ||
    null;
}

// ============================================================================
// Office Hours
// ============================================================================

// Office hours store the day as a 3-letter uppercase abbreviation
// (MON, TUE, ...) — see OfficeHours.DAY_MAPPINGS in global/models/collectionObjects.js.
// schema.org's dayOfWeek only accepts the DayOfWeek enum URLs, so we key the
// map by the first three letters and also accept full names defensively.
const DAY_MAP: Record<string, string> = {
  mon: 'https://schema.org/Monday',
  tue: 'https://schema.org/Tuesday',
  wed: 'https://schema.org/Wednesday',
  thu: 'https://schema.org/Thursday',
  fri: 'https://schema.org/Friday',
  sat: 'https://schema.org/Saturday',
  sun: 'https://schema.org/Sunday',
};

function toDayOfWeek(day: string): string | null {
  return DAY_MAP[day.trim().slice(0, 3).toLowerCase()] ?? null;
}

export function buildOpeningHours(officeHours: OfficeHours[] | undefined | null): Record<string, unknown>[] | null {
  if (!officeHours?.length) return null;

  const specs: Record<string, unknown>[] = [];
  for (const h of officeHours) {
    if (!defined(h.day) || !defined(h.open_time) || !defined(h.close_time)) continue;
    const dayOfWeek = toDayOfWeek(h.day);
    if (!dayOfWeek) continue;
    specs.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek,
      opens: h.open_time,
      closes: h.close_time,
    });
  }
  return specs;
}

// ============================================================================
// Reviews / Aggregate Rating
// ============================================================================

export function buildAggregateRating(reviews: Review[] | undefined | null): Record<string, unknown> | null {
  if (!reviews?.length) return null;

  const rated = reviews.filter(r => num(r.rating) != null && num(r.rating)! > 0);
  if (!rated.length) return null;

  const total = rated.reduce((sum, r) => sum + (num(r.rating) || 0), 0);
  const avg = Math.round((total / rated.length) * 10) / 10;

  return {
    '@type': 'AggregateRating',
    ratingValue: avg,
    reviewCount: rated.length,
    bestRating: 5,
    worstRating: 1,
  };
}

export function buildReviews(reviews: Review[] | undefined | null): Record<string, unknown>[] | null {
  if (!reviews?.length) return null;

  return reviews
    .filter(r => defined(r.text))
    .slice(0, 10)
    .map(r => {
      const review: Record<string, unknown> = {
        '@type': 'Review',
        reviewBody: r.text,
      };
      if (defined(r.name)) review.author = { '@type': 'Person', name: r.name };
      const ratingNum = num(r.rating);
      if (ratingNum) review.reviewRating = { '@type': 'Rating', ratingValue: ratingNum, bestRating: 5 };
      if (defined(r.date)) review.datePublished = r.date;
      return review;
    });
}

// ============================================================================
// Amenities
// ============================================================================

export function buildAmenities(amenities: Amenity[] | undefined | null): Record<string, unknown>[] | null {
  if (!amenities?.length) return null;

  return amenities
    .filter(a => defined(a.title))
    .map(a => {
      const feature: Record<string, unknown> = {
        '@type': 'LocationFeatureSpecification',
        name: a.title,
        value: true,
      };
      if (defined(a.description)) feature.description = a.description;
      return feature;
    });
}

// ============================================================================
// Floorplans
// ============================================================================

export function buildFloorplans(floorplans: Floorplan[] | undefined | null): Record<string, unknown>[] | null {
  if (!floorplans?.length) return null;

  return floorplans
    .filter(fp => defined(fp.floorplan_name))
    .map(fp => {
      // Multi-typed entity: Accommodation carries the physical attributes
      // (beds/baths/size) while Product legitimises the `offers` property —
      // this is the pattern schema.org documents for lodging with pricing.
      const accommodation: Record<string, unknown> = {
        '@type': ['Accommodation', 'Product'],
        name: fp.floorplan_name,
      };

      const beds = num(fp.floorplan_beds_min);
      if (beds != null) accommodation.numberOfBedrooms = beds;

      const baths = num(fp.floorplan_baths_min);
      if (baths != null) accommodation.numberOfBathroomsTotal = baths;

      // floorSize.value must be a single number. For a range we express it with
      // minValue/maxValue rather than a "min-max" string (which is invalid).
      const sqftMin = num(fp.floorplan_sqft_min);
      const sqftMax = num(fp.floorplan_sqft_max);
      if (sqftMin) {
        const hasRange = !!sqftMax && sqftMax !== sqftMin;
        accommodation.floorSize = {
          '@type': 'QuantitativeValue',
          unitCode: 'FTK',
          ...(hasRange
            ? { minValue: sqftMin, maxValue: sqftMax }
            : { value: sqftMin }),
        };
      }

      const priceMin = num(fp.floorplan_price_min);
      const priceMax = num(fp.floorplan_price_max);
      if (priceMin) {
        accommodation.offers = {
          '@type': 'Offer',
          priceCurrency: 'USD',
          price: priceMin,
          ...(priceMax && priceMax !== priceMin ? { highPrice: priceMax } : {}),
        };
      }

      const image = mediaUrl(fp.floorplan_image_url);
      if (defined(image)) accommodation.image = image;

      return accommodation;
    });
}

// ============================================================================
// Photo Gallery
// ============================================================================

export function buildGallery(gallery: PhotoGallery[] | undefined | null): Record<string, unknown>[] | null {
  if (!gallery?.length) return null;

  return gallery
    .filter(p => defined(p.image))
    .slice(0, 20)
    .map(p => {
      const img: Record<string, unknown> = {
        '@type': 'ImageObject',
        url: mediaUrl(p.image),
      };
      if (defined(p.alt_text)) img.name = p.alt_text;
      if (defined(p.description)) img.description = p.description;
      return img;
    });
}

// ============================================================================
// Special Offers
// ============================================================================

export function buildSpecialOffers(offers: SpecialOffer[] | undefined | null): Record<string, unknown>[] | null {
  if (!offers?.length) return null;

  const active = offers.filter(o => o.enabled !== 'false' && defined(o.title));
  if (!active.length) return null;

  return active.map(o => {
    const offer: Record<string, unknown> = {
      '@type': 'Offer',
      name: o.title,
    };
    if (defined(o.description)) offer.description = o.description;
    if (defined(o.start_date)) offer.validFrom = o.start_date;
    if (defined(o.end_date)) offer.validThrough = o.end_date;
    return offer;
  });
}

// ============================================================================
// Full Schema Assembler
// ============================================================================

export interface SchemaToggles {
  includeReviews: boolean;
  includeFloorplans: boolean;
  includeAmenities: boolean;
  includeOfficeHours: boolean;
  includeGallery: boolean;
  includeSpecialOffers: boolean;
}

export interface JsonLdLiveCollections {
  floorplans?: Floorplan[] | null;
  specialOffers?: SpecialOffer[] | null;
}

export function buildFullSchema(
  property: Property,
  types: string[],
  overrides: CoreOverrides,
  toggles: SchemaToggles,
  live: JsonLdLiveCollections = {},
): Record<string, unknown> {
  const schema = buildCoreSchema(property, types, overrides);

  const address = buildAddress(property);
  if (address) schema.address = address;

  const geo = buildGeo(property);
  if (geo) schema.geo = geo;

  const sameAs = buildSocialLinks(property);
  if (sameAs) schema.sameAs = sameAs;

  const hasMap = buildHasMap(property);
  if (hasMap) schema.hasMap = hasMap;

  if (toggles.includeOfficeHours) {
    const hours = buildOpeningHours(property.office_hours);
    if (hours?.length) schema.openingHoursSpecification = hours;
  }

  if (toggles.includeReviews) {
    const aggregate = buildAggregateRating(property.reviews);
    if (aggregate) schema.aggregateRating = aggregate;

    const reviews = buildReviews(property.reviews);
    if (reviews?.length) schema.review = reviews;
  }

  if (toggles.includeAmenities) {
    const amenities = buildAmenities(property.amenities);
    if (amenities?.length) schema.amenityFeature = amenities;
  }

  if (toggles.includeFloorplans) {
    const floorplans = buildFloorplans(live.floorplans);
    if (floorplans?.length) schema.containsPlace = floorplans;
  }

  if (toggles.includeGallery) {
    const gallery = buildGallery(property.photo_gallery);
    if (gallery?.length) schema.photo = gallery;
  }

  if (toggles.includeSpecialOffers) {
    const offers = buildSpecialOffers(live.specialOffers);
    if (offers?.length) schema.makesOffer = offers;
  }

  return schema;
}
